import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { opinlyAnonId } from "@/lib/opinly-analytics";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  createPaymentCheckout,
  getBookingForPayment,
  type PublicBookingForPayment,
} from "@/lib/payment.functions";

export const Route = createFileRoute("/pay/$token")({
  head: () => ({
    meta: [
      { title: "Secure payment, Casa Mi Pajobe" },
      {
        name: "description",
        content:
          "Securely pay the deposit or balance for your Casa Mi Pajobe booking via this private payment link.",
      },
      { property: "og:title", content: "Secure payment, Casa Mi Pajobe" },
      {
        property: "og:description",
        content: "Private payment link for your Casa Mi Pajobe villa booking.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PayPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground"
        >
          Try again
        </button>
      </Shell>
    );
  },
  notFoundComponent: () => (
    <Shell>
      <h1 className="text-2xl font-semibold">Payment link not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This link may have expired. Please contact us if you need a new one.
      </p>
    </Shell>
  ),
});

function fmt(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function PayPage() {
  const { token } = Route.useParams();
  const search = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const status = search.get("status");

  const fetchBooking = useServerFn(getBookingForPayment);
  const startCheckout = useServerFn(createPaymentCheckout);

  const q = useQuery({
    queryKey: ["payment-booking", token],
    queryFn: () => fetchBooking({ data: { token } }),
    refetchOnWindowFocus: false,
  });

  const mut = useMutation({
    mutationFn: startCheckout,
  });

  useEffect(() => {
    if (mut.data && "url" in mut.data && mut.data.url) {
      window.location.href = mut.data.url;
    }
  }, [mut.data]);

  if (q.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </Shell>
    );
  }

  if (!q.data || ("notFound" in q.data && q.data.notFound)) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Payment link not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This link may have expired. Please contact us if you need a new one.
        </p>
      </Shell>
    );
  }

  const b = q.data as PublicBookingForPayment;
  const isDeposit = b.kind === "deposit";
  const label = isDeposit ? "Deposit (25%)" : "Final balance (75%)";

  return (
    <Shell>
      <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        Casa Mi Pajobe
      </div>
      <h1 className="font-display text-3xl font-semibold">{label}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Booking <span className="font-mono">{b.reference}</span> · {b.guestName}
      </p>

      <div className="mt-6 space-y-3 rounded-2xl border border-border bg-card p-5 text-sm">
        <Row k="Check-in" v={b.checkIn} />
        <Row k="Check-out" v={b.checkOut} />
        <Row k="Total stay" v={fmt(b.totalCents, b.currency)} />
        <Row k="Deposit (25%)" v={fmt(b.depositCents, b.currency)} />
        <Row
          k="Balance (75%)"
          v={
            <>
              {fmt(b.balanceCents, b.currency)}
              {b.balanceDueDate && (
                <span className="ml-2 text-xs text-muted-foreground">
                  due by {b.balanceDueDate}
                </span>
              )}
            </>
          }
        />
      </div>

      {status === "success" && (
        <div className="mt-6 rounded-xl border border-green-500/40 bg-green-500/10 p-4 text-sm">
          Thank you, we received your payment confirmation. You'll get an email shortly.
        </div>
      )}
      {status === "cancelled" && (
        <div className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          Payment was cancelled. You can try again below.
        </div>
      )}

      {b.paid ? (
        <div className="mt-6 rounded-xl border border-green-500/40 bg-green-500/10 p-5">
          <div className="text-sm font-semibold text-green-700 dark:text-green-300">
            {label} received
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {b.paidAt && `Paid on ${b.paidAt.slice(0, 10)}.`}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex items-end justify-between rounded-2xl border border-border bg-muted/40 p-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Amount due now
              </div>
              <div className="mt-1 font-display text-3xl font-semibold">
                {fmt(b.amountDueCents, b.currency)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Local VAT/tourist tax will be calculated at checkout.
              </div>
            </div>
            <button
              disabled={mut.isPending || mut.isSuccess}
              onClick={() => {
                const anonId = opinlyAnonId();
                mut.mutate({ data: { token, ...(anonId ? { anonId } : {}) } });
              }}
              className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60"
            >
              {mut.isPending || mut.isSuccess ? "Redirecting…" : "Pay securely"}
            </button>
          </div>
          {mut.data && "error" in mut.data && (
            <p className="mt-3 text-sm text-destructive">{mut.data.error}</p>
          )}
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Secured by Stripe. We never see your card details.
          </p>
        </>
      )}
    </Shell>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-xl px-6 py-16">{children}</div>
    </div>
  );
}